import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Bell } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import type { OrganizationSettings } from '../hooks/useSettings';

interface NotificationSettingsProps {
  settings: OrganizationSettings['notifications'] | null;
  isLoading: boolean;
  onSave: (updates: Partial<OrganizationSettings['notifications']>) => Promise<boolean | undefined>;
}

interface NotificationToggleProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
}

function NotificationToggle({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}: NotificationToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div className="space-y-0.5">
        <label htmlFor={id} className="text-sm font-medium">
          {label}
        </label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
          disabled={disabled}
        />
        <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
      </label>
    </div>
  );
}

export function NotificationSettings({ settings, isLoading, onSave }: NotificationSettingsProps) {
  const { t } = useTranslation();
  const [emailNotifications, setEmailNotifications] = useState(
    settings?.emailNotifications ?? true
  );
  const [activityAlerts, setActivityAlerts] = useState(settings?.activityAlerts ?? true);
  const [securityAlerts, setSecurityAlerts] = useState(settings?.securityAlerts ?? true);
  const [weeklyDigest, setWeeklyDigest] = useState(settings?.weeklyDigest ?? false);
  const [marketingEmails, setMarketingEmails] = useState(settings?.marketingEmails ?? false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setEmailNotifications(settings.emailNotifications);
      setActivityAlerts(settings.activityAlerts);
      setSecurityAlerts(settings.securityAlerts);
      setWeeklyDigest(settings.weeklyDigest);
      setMarketingEmails(settings.marketingEmails);
    }
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave({
        emailNotifications,
        activityAlerts,
        securityAlerts,
        weeklyDigest,
        marketingEmails,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          {t('settings.notifications')}
        </CardTitle>
        <CardDescription>{t('settings.notificationsDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <NotificationToggle
          id="emailNotifications"
          label={t('settings.emailNotifications')}
          description={t('settings.emailNotificationsDescription')}
          checked={emailNotifications}
          onChange={setEmailNotifications}
          disabled={isLoading}
        />

        <NotificationToggle
          id="activityAlerts"
          label={t('settings.activityAlerts')}
          description={t('settings.activityAlertsDescription')}
          checked={activityAlerts}
          onChange={setActivityAlerts}
          disabled={isLoading}
        />

        <NotificationToggle
          id="securityAlerts"
          label={t('settings.securityAlerts')}
          description={t('settings.securityAlertsDescription')}
          checked={securityAlerts}
          onChange={setSecurityAlerts}
          disabled={isLoading}
        />

        <NotificationToggle
          id="weeklyDigest"
          label={t('settings.weeklyDigest')}
          description={t('settings.weeklyDigestDescription')}
          checked={weeklyDigest}
          onChange={setWeeklyDigest}
          disabled={isLoading}
        />

        <NotificationToggle
          id="marketingEmails"
          label={t('settings.marketingEmails')}
          description={t('settings.marketingEmailsDescription')}
          checked={marketingEmails}
          onChange={setMarketingEmails}
          disabled={isLoading}
        />

        <Button onClick={handleSave} disabled={isLoading || isSaving}>
          {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </CardContent>
    </Card>
  );
}
